import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { StyleSheet, ScrollView } from 'react-native';
import { v4 as uuid } from 'uuid';
import { Page } from '@alucio/aws-beacon-amplify/src/models'
import { DNABox, DNAButton, DNACheckbox, DNAContextMenu, DNAText, Iffy, Stack } from '@alucio/lux-ui';
import colors from '@alucio/lux-ui/src/theming/themes/alucio/colors'
import {
  AssociatedSlidesThumbnails,
  DNAChip,
  DNAPopover,
  DNAThumbnail,
} from 'src/components/DNA';
import { ThumbnailPage } from './SlideSelector'
import { ThumbnailSize } from 'src/hooks/useThumbnailSize/useThumbnailSize'
import { DocumentVersionORM } from 'src/types/types'
import { PayloadGroup } from '../PresentationBuilder/state/PresentationBuilderStateProvider'
import { useCurrentUser } from 'src/state/redux/selector/user'
import CollapsibleCard, { CollapsibleActionMenu } from '../CollapsibleCard/CollapsibleCard'
import { getPresentable } from 'src/state/context/ContentProvider/helper';
import useContentPageData,
{ ContentPageData, getSlideContentPageDataTitle } from 'src/hooks/useContentPageData/useContentPageData';
import { SortableItem, SortableItemProps } from '../DnD/DnDWrapper';
import logger from 'src/utils/logger';
import { sortGroups } from 'src/utils/groupHelpers';
import { canToggleHiddenSlide } from 'src/utils/hiddenSlides';
import { canSelectIndividualSlides } from 'src/utils/customDeckRules/eligibility';

interface SectionConfig {
  /** NOTE: Disabling the section will also disable the header since
   * there is no real value in having a sectionless header visible */
  disableSection?: boolean
  disableSectionHeader?: boolean
}

export interface DisplayConfig {
  groups?: SectionConfig
  individualSlides?: SectionConfig
}

interface PageGroupListProps {
  handleGroupSelect: (group: GroupDraft) => void
  selectorThumbnailSize: ThumbnailSize
  allGroups: GroupDraft[]
  disableCheckboxes?: boolean
  documentVersionORM?: DocumentVersionORM
  selectedGroups?: PayloadGroup[],
  hideGroups?: boolean,
  hideAssociatedSlides?: boolean,
  onToggleHiddenSlide?: (page: ThumbnailPage) => void,
}

export interface GroupDraft {
  id: string;
  pages: ThumbnailPage[];
  name?: string,
  locked?: boolean,
  checked?: boolean,
}

const styles = StyleSheet.create({
  allSlides: {
    backgroundColor: colors['color-gray-50'],
    height: 48,
    borderRadius: 4,
    marginBottom: 24,
  },
});

const RightGroupMenu: React.FC<{
  group: GroupDraft,
  onCheck: () => void,
  checked: boolean,
}> = ({ onCheck, group, checked }) => {
  return (
    <DNABox spacing="between" alignY="center">
      <Iffy is={group}>
        <DNABox alignX="center" alignY="center" fill childFill>
          <DNAChip.Beacon children={`${group?.pages.length}`} />
        </DNABox>
        <DNAPopover>
          <DNAPopover.Anchor>
            <DNAButton
              appearance="ghost"
              status="subtle"
              iconLeft={group.locked ? 'lock' : 'lock-simple-open'}
              rounded
              disabled
            />
          </DNAPopover.Anchor>
          <DNAPopover.Content>
            <DNAText style={{ color: colors['color-text-white'] }}>
              {group.locked ? 'Ungrouping restricted' : 'Ungrouping allowed'}
            </DNAText>
          </DNAPopover.Content>
        </DNAPopover>
      </Iffy>
      <DNACheckbox
        checked={checked}
        status="gray"
        onChange={onCheck}
      />
    </DNABox>
  )
}

export const s3Size : Record<ThumbnailSize, string> = {
  'xxs': 'small',
  'xs': 'small',
  'sm': 'small',
  'md': 'small',
  'lg': 'small',
  'xl': 'medium',
  'xxl': 'medium',
}

const useSlideTitleGetter = (
  contentPageData?: ContentPageData[],
  pageNumber?: number,
  title?: string,
) => {
  return useCallback((num: number): string => {
    try {
      if (!contentPageData || !pageNumber) {
        return `Slide ${pageNumber || 'Not found'}`;
      }

      if (!num || num < 1) {
        logger.customDeck.error('Invalid slide number:', num);
        return `Slide ${num || 'Not found'}`;
      }

      if (num === pageNumber) {
        return title || `Slide ${pageNumber}`;
      }

      const slideData = contentPageData.find(pageData => pageData.documentPageNumber === num);
      return slideData?.title || `Slide ${num} of ${contentPageData.length}`;
    } catch (error) {
      logger.customDeck.error('Error getting slide title:', error);
      return `Slide ${num || 'Not found'}`;
    }
  }, [contentPageData, pageNumber, title]);
};

export const SlideThumb: React.FC<{
  page: ThumbnailPage,
  documentVersionORM?: DocumentVersionORM,
  previewPages?: Page[],
  onCheck?: () => void,
  showAssociation: boolean
  selectorThumbnailSize: ThumbnailSize,
  title?: string,
  isInsideGroup?: boolean,
  contentPageData?: ContentPageData[],
  onToggleHiddenSlide?: (page: ThumbnailPage) => void,
}> = (props) => {
  const {
    page,
    documentVersionORM,
    previewPages,
    onCheck,
    showAssociation,
    selectorThumbnailSize,
    title,
    isInsideGroup = false,
    contentPageData,
    onToggleHiddenSlide,
  } = props

  const getSlideTitle = useSlideTitleGetter(contentPageData, page.number, title);

  // Prepare preview data for magnify functionality
  const preview = useMemo(() => {
    if (!documentVersionORM) return undefined;

    return {
      pages: previewPages ?? documentVersionORM.meta.allPages,
      docVerORM: documentVersionORM,
      getSlideTitle,
    };
  }, [documentVersionORM, getSlideTitle, previewPages])

  const basePath = documentVersionORM?.meta
    .assets
    .thumbnailKey
    ?.split('/')
    .slice(0, -1)
    .join('/');
  const thumbURL = basePath ? `${basePath}/${page.number}_${s3Size[selectorThumbnailSize]}_thumb.png` : ''
  const canToggleHidden = !!onToggleHiddenSlide && (canToggleHiddenSlide(page) || !!page.isHidden);
  const toggleHiddenTitle = page.isHidden ? 'Unhide slide' : 'Hide slide';

  const associatedPages: Page[] = []
  page.linkedSlides?.forEach(slideId => {
    const page = documentVersionORM?.meta.allPages.find(page => page.pageId === slideId)
    if (page) associatedPages.push(page)
  })

  return (
    <DNABox
      alignY="start"
      alignX="center"
      appearance="col"
      style={{ marginBottom: 18 }}
    >
      <DNABox style={{ marginHorizontal: 6 }}>
        <Stack>
          <Stack.Layer>
            <DNAThumbnail.Lazy
              s3URL={thumbURL}
              variant={DNAThumbnail.Variants.INFO}
              magnifyConfig={isInsideGroup
                ? DNAThumbnail.MagnifyPresets.SINGLE
                : DNAThumbnail.MagnifyPresets.WITH_NAVIGATION}
              preview={preview}
              mode={onCheck ? DNAThumbnail.Modes.SELECTABLE : undefined}
              size={selectorThumbnailSize}
              pageNumber={page.number}
              useLoadingIndicator
              isRequired={page?.isRequired}
              isHidden={page?.isHidden}
              sourceIsHidden={page?.sourceIsHidden}
              onCheck={onCheck}
              checked={onCheck ? page.checked : undefined}
              thumbnailTitle={title || getSlideTitle(page.number)}
            />
          </Stack.Layer>
          <Stack.Layer anchor="topRight" style={{ marginTop: 6, marginRight: 6 }}>
            <Iffy is={canToggleHidden}>
              <DNAContextMenu placement="bottom end">
                <DNAContextMenu.Anchor>
                  <DNAButton
                    status="transparent"
                    iconLeft="dots-three-vertical"
                  />
                </DNAContextMenu.Anchor>
                <DNAContextMenu.Items>
                  <DNAContextMenu.Item
                    title={toggleHiddenTitle}
                    icon={page.isHidden ? 'eye' : 'prohibit'}
                    onPress={() => onToggleHiddenSlide?.(page)}
                  />
                </DNAContextMenu.Items>
              </DNAContextMenu>
            </Iffy>
          </Stack.Layer>
        </Stack>
      </DNABox>
      {documentVersionORM && showAssociation
        ? <AssociatedSlidesThumbnails
            docVerORM={documentVersionORM}
            pages={associatedPages}
            size="lg"
            getSlideTitle={getSlideContentPageDataTitle}
        />
        : null}
    </DNABox>
  )
}

interface IdedGroupPage extends ThumbnailPage {
  uuid: string
}

export const CollapsibleGroupItem : React.FC<{
  page: IdedGroupPage,
  contentPageData?: ContentPageData[],
  group: GroupDraft,
  sortableHeader?: SortableItemProps
  documentVersionORM?: DocumentVersionORM,
  selectorThumbnailSize: ThumbnailSize,
  onSlideCheck: (page: IdedGroupPage) => void,
  onToggleHiddenSlide?: (page: ThumbnailPage) => void,
}> = (props) => {
  const {
    contentPageData,
    documentVersionORM,
    group,
    onSlideCheck,
    page,
    selectorThumbnailSize,
    sortableHeader,
    onToggleHiddenSlide,
  } = props
  const title = contentPageData ? contentPageData[page.number - 1]?.title : `Slide ${page.number}`

  const SlideThumbContainer = useMemo(() => ({ children }) => (
    group.locked || group.checked
      ? <>
        {children}
      </>
      : <SortableItem
          id={uuid()}
          itemId={page.pageId}
          containerId={sortableHeader?.containerId || ''}
      >
        {children}
      </SortableItem>
  ), [group.locked, page.pageId, sortableHeader?.containerId])

  const onCheck = useCallback(() => {
    onSlideCheck(page)
  }, [onSlideCheck, page])

  const id = React.useMemo(() => uuid(), [])

  return (
    <SlideThumbContainer>
      <DNABox appearance="col" style={{ marginHorizontal: 6 }}>
        <SlideThumb
          key={id}
          page={page}
          documentVersionORM={documentVersionORM}
          showAssociation={!!group.checked}
          selectorThumbnailSize={selectorThumbnailSize}
          title={title}
          onCheck={group.checked || group.locked ? undefined : onCheck}
          isInsideGroup={true}
          onToggleHiddenSlide={onToggleHiddenSlide}
        />
      </DNABox>
    </SlideThumbContainer>
  )
}

export const CollapsibleGroupCard: React.FC<{
  group: GroupDraft,
  documentVersionORM?: DocumentVersionORM,
  onCheck: () => void,
  selectorThumbnailSize: ThumbnailSize,
  contentPageData?: ContentPageData[],
  sortableHeader?: SortableItemProps
  handleSelectSlide?: (page: Page) => void
  onToggleHiddenSlide?: (page: ThumbnailPage) => void
  isCollapsed?: boolean
  deckSearchText?: string
}> = (props) => {
  const {
    group, documentVersionORM, onCheck,
    selectorThumbnailSize, sortableHeader, contentPageData, isCollapsed, deckSearchText, onToggleHiddenSlide,
  } = props

  // An array of uuids that does not change when the group changes and provokes a re-render
  const uuidArray = useRef<string[] | undefined>(undefined)
  // The function passed to useEffect will run after the render is committed to the screen
  // We use this boolean state to detect when there is change of the uuidArray so we can properly update memoIdGroupPages
  const [triggerRerender, setTriggerRerender] = useState<boolean>(true)

  useEffect(() => {
    if (uuidArray.current) return
    uuidArray.current = group.pages.map(() => uuid())
    setTriggerRerender(s => !s)
  }, [group])

  // TODO: consider making this an interface that is used on hydration to generate unique IDs
  // since we now have the possibility of having duplicate slides in groups.
  const memoIdGroupPages = useMemo(() => {
    const idedGroupPages: IdedGroupPage[] = group.pages.map((page, idx) => {
      const updatedIdedPage: IdedGroupPage = { ...page, uuid: uuidArray.current?.[idx] || `pending-${idx}` }
      return updatedIdedPage
    })
    return idedGroupPages
  }, [group, triggerRerender])

  const onSlideCheck = useCallback((page: IdedGroupPage) => {
    if (group.locked) return

    props?.handleSelectSlide?.(page)
  }, [group])

  const groupItems = useMemo(() => {
    let filteredPages = memoIdGroupPages

    if (deckSearchText && deckSearchText.trim().length > 0) {
      const normalizedSearch = deckSearchText.toLowerCase()
      filteredPages = memoIdGroupPages.filter(page => {
        const title = contentPageData?.[page.number - 1]?.title || ''
        const content = contentPageData?.[page.number - 1]?.content || ''
        const titleMatch = title && title.toLowerCase().includes(normalizedSearch)
        const contentMatch = content && content.toLowerCase().includes(normalizedSearch)
        return titleMatch || contentMatch
      })
    }

    return filteredPages.map((page) => {
      return (
        <CollapsibleGroupItem
          key={page.uuid}
          page={page}
          contentPageData={contentPageData}
          group={group}
          documentVersionORM={documentVersionORM}
          selectorThumbnailSize={selectorThumbnailSize}
          onSlideCheck={onSlideCheck}
          onToggleHiddenSlide={onToggleHiddenSlide}
          sortableHeader={sortableHeader}
        />
      )
    })
  }, [group, documentVersionORM, selectorThumbnailSize, contentPageData,
    sortableHeader, memoIdGroupPages, deckSearchText])

  return (
    <CollapsibleCard
      contentPadding="none"
      headerTitle={group.name || ''}
      isCollapsed={isCollapsed}
      numberOfTitleLines={1}
      selected={!!group.checked}
      sortableHeader={sortableHeader}
    >
      <CollapsibleActionMenu>
        <RightGroupMenu
          group={group}
          onCheck={onCheck}
          checked={!!group.checked}
        />
      </CollapsibleActionMenu>
      <DNABox wrap="start" alignX="center" style={{ paddingVertical: 18 }}>
        {groupItems}
      </DNABox>
    </CollapsibleCard>
  )
}

const PageGroupList: React.FC<PageGroupListProps> = ({
  handleGroupSelect,
  documentVersionORM,
  allGroups,
  selectedGroups,
  selectorThumbnailSize,
  hideGroups,
  hideAssociatedSlides,
  onToggleHiddenSlide,
}) => {
  const currentUser = useCurrentUser()
  const canSelectSlides = documentVersionORM && canSelectIndividualSlides(
    documentVersionORM,
    currentUser.authProfile?.attributes.email,
  )

  const ContainerRef = useRef<ScrollView>(null)
  const presentable = getPresentable(documentVersionORM);
  const { contentPageData } = useContentPageData(presentable, {
    disableCache: true,
    pagesJsonNetworkFirst: true,
  })
  const collapsibleGroups: GroupDraft[] = []
  const allIndividualSlideGroups: GroupDraft[] = []
  allGroups.forEach(groupDraft => {
    const { pages: { length } } = groupDraft
    length > 1
      ? collapsibleGroups.push(groupDraft)
      : allIndividualSlideGroups.push(groupDraft)
  })

  const { groups, individualSlides }:DisplayConfig = {
    groups: {
      disableSection: hideGroups,
    },
    individualSlides: {
      disableSectionHeader: hideGroups,
    },
  }

  const handleForcedSelectedCheck = (group: GroupDraft) => {
    let isForcedSelected: boolean = group.pages.some(page => page.isRequired)
    // Check if the selectedGroups already has the required slide(s)
    if (isForcedSelected) {
      selectedGroups?.forEach(group => {
        group.pages.forEach(slide => {
          const requiredPage = group.pages.find(page => page.isRequired)
          // If the selectedGroups has the required slide already, then no need to force select
          if (slide.pageId === requiredPage?.pageId) isForcedSelected = false
        })
      })
    }
    // Check if the current selected/checked group has the required slides
    if (isForcedSelected) {
      collapsibleGroups.forEach(collapsibleGroup => {
        if (collapsibleGroup.checked) {
          collapsibleGroup.pages.forEach(slide => {
            const requiredPage = group.pages.find(page => page.isRequired)
            if (slide.pageId === requiredPage?.pageId) {
              isForcedSelected = false
              requiredPage.checked = false
            }
          })
        }
      })
    }
    // If the selectGroups does not hve required slide, then modified the slide to be checked
    if (isForcedSelected) {
      group.pages.forEach(page => {
        page.checked = true
      })
    }
  }

  const handleSelectionSlide = useCallback((page: Page): void => {
    const group = allIndividualSlideGroups
      .find(group => group.pages.find(pageWithGroup => pageWithGroup.pageId === page.pageId));

    if (!group) return;

    handleGroupSelect(group);
  }, [allIndividualSlideGroups, handleGroupSelect]);

  return (
    <DNABox as={ScrollView} appearance="col" ref={ContainerRef}>
      {/* All the groups */}
      {!groups?.disableSection && !!collapsibleGroups.length &&
        <DNABox appearance="col">

          {/* Header */}
          {!groups?.disableSectionHeader &&
            <DNABox alignX="center" alignY="center" style={styles.allSlides}>
              <DNAText h5>Groups</DNAText>
            </DNABox>
          }

          {/* Groups */}
          {collapsibleGroups
            .sort(sortGroups())
            ?.map(group => {
              return (
                <DNABox style={{ marginBottom: 24 }} key={group.id}>
                  <CollapsibleGroupCard
                    key={group.id}
                    group={group}
                    documentVersionORM={documentVersionORM}
                    onCheck={() => handleGroupSelect(group)}
                    selectorThumbnailSize={selectorThumbnailSize}
                    handleSelectSlide={handleSelectionSlide}
                    onToggleHiddenSlide={onToggleHiddenSlide}
                    contentPageData={contentPageData}
                  />
                </DNABox>
              )
            })}        </DNABox>}

      {/* All individual slides */}
      {!individualSlides?.disableSection && canSelectSlides &&
        <DNABox appearance="col">

          {/* Header */}
          {!individualSlides?.disableSectionHeader &&
            <DNABox alignX="center" alignY="center" style={styles.allSlides}>
              <DNAText h5>All Slides</DNAText>
            </DNABox>}

          {/* Slides */}
          <DNABox wrap="start" alignX="center">
            {allIndividualSlideGroups.map(group => {
              handleForcedSelectedCheck(group)
              return group.pages.map(page => {
                const title = contentPageData.length > 0
                  ? getSlideContentPageDataTitle(page.number - 1, contentPageData) : ''
                return (
                  <DNABox key={`single-slide-${page.pageId}`} style={{ marginRight: 12 }}>
                    <SlideThumb
                      page={page}
                      documentVersionORM={documentVersionORM}
                      title={title}
                      onCheck={!page.isPlaceholder
                        ? () => { handleGroupSelect(group) }
                        : undefined}
                      showAssociation={!hideAssociatedSlides && page.checked}
                      selectorThumbnailSize={selectorThumbnailSize}
                      onToggleHiddenSlide={onToggleHiddenSlide}
                    />
                  </DNABox>
                )
              })
            })}
          </DNABox>
        </DNABox>
      }

    </DNABox>
  )
}

export default PageGroupList
